import React, { useState } from 'react';
import { X } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-244">
      <div className="absolute inset-0 bg-base/80 backdrop-blur-sm" data-id="element-245" />
      <div className="relative bg-surface border border-border-accent rounded-sm max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-gold-subtle" onClick={e => e.stopPropagation()} data-id="element-246">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-default" data-id="element-247">
          <h2 className="font-display text-xl font-bold text-text-primary" data-id="element-248">
            {title}
          </h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors p-1 cursor-pointer" aria-label="Close" data-id="element-249">
            <X className="w-5 h-5" data-id="element-250" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(80vh-80px)] font-body text-sm text-text-secondary leading-relaxed space-y-4" data-id="element-251">
          {children}
        </div>
      </div>
    </div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'terms' | 'privacy' | null>(null);
  return <>
      <footer className="py-12 px-6 bg-surface border-t border-border-default text-center" data-id="element-252">
        <div className="max-w-4xl mx-auto" data-id="element-253">
          <FadeIn data-id="element-254">
            <p className="font-display text-accent-muted text-xl font-bold tracking-wide mb-4" data-id="element-255">
              The Voice They Couldn't Silence
            </p>
            <p className="font-ui text-xs text-text-muted uppercase tracking-wider" data-id="element-256">
              © 2026 The Voice They Couldn't Silence. All rights reserved.
            </p>
            <div className="mt-8 flex justify-center gap-6 font-ui text-xs text-text-muted" data-id="element-257">
              <button onClick={() => setActiveModal('terms')} className="hover:text-text-primary transition-colors cursor-pointer bg-transparent border-none font-ui text-xs text-text-muted" data-id="element-258">
                Terms of Service
              </button>
              <button onClick={() => setActiveModal('privacy')} className="hover:text-text-primary transition-colors cursor-pointer bg-transparent border-none font-ui text-xs text-text-muted" data-id="element-259">
                Privacy Policy
              </button>
            </div>
          </FadeIn>
        </div>
      </footer>

      {/* Terms of Service Modal */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms of Service" data-id="element-260">
        <p className="font-ui text-xs text-text-muted uppercase tracking-wider" data-id="element-261">
          Last updated: February 2026
        </p>
        <p data-id="element-262">
          By purchasing and accessing "The Voice They Couldn't Silence" (the
          "Product"), you agree to the following terms and conditions.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-263">
          1. Digital Product License
        </h3>
        <p data-id="element-264">
          Upon purchase, you receive a personal, non-transferable, non-exclusive
          license to access and read the digital content. This license is for
          personal use only.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-265">
          2. Intellectual Property
        </h3>
        <p data-id="element-266">
          All content, design, and materials within the Product are protected by
          copyright. You may not reproduce, distribute, modify, or create
          derivative works without prior written consent.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-267">
          3. Refund Policy
        </h3>
        <p data-id="element-268">
          Due to the digital nature of the Product, all sales are final. If you
          experience technical issues accessing your purchase, please reach out
          and we will resolve the matter promptly.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-269">
          4. Limitation of Liability
        </h3>
        <p data-id="element-270">
          The Product is provided "as is." We make no warranties regarding the
          accuracy or completeness of the content. Our liability is limited to
          the purchase price of the Product.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-271">
          5. Governing Law
        </h3>
        <p data-id="element-272">
          These terms shall be governed by and construed in accordance with
          applicable laws. Any disputes shall be resolved through binding
          arbitration.
        </p>
      </LegalModal>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" data-id="element-273">
        <p className="font-ui text-xs text-text-muted uppercase tracking-wider" data-id="element-274">
          Last updated: February 2026
        </p>
        <p data-id="element-275">
          Your privacy matters. This policy explains what information we
          collect, how we use it, and your rights regarding your data.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-276">
          1. Information We Collect
        </h3>
        <p data-id="element-277">
          We collect only the information necessary to process your purchase:
          email address and payment details. Payment processing is handled by
          secure third-party providers — we never store your card information.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-278">
          2. How We Use Your Information
        </h3>
        <p data-id="element-279">
          Your email is used solely to deliver the Product and provide
          purchase-related communications. We do not sell, rent, or share your
          personal information with third parties for marketing purposes.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-280">
          3. Data Security
        </h3>
        <p data-id="element-281">
          We implement industry-standard security measures to protect your
          personal information. All transactions are encrypted using SSL
          technology.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-282">
          4. Cookies
        </h3>
        <p data-id="element-283">
          We may use essential cookies to ensure the proper functioning of our
          website. No tracking or advertising cookies are used.
        </p>
        <h3 className="font-display text-base font-bold text-text-primary pt-2" data-id="element-284">
          5. Your Rights
        </h3>
        <p data-id="element-285">
          You have the right to request access to, correction of, or deletion of
          your personal data at any time. To exercise these rights, please reach
          out through the appropriate channels.
        </p>
      </LegalModal>
    </>;
}